import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ExternalLink, Calendar, Tag, Play, FileText, Newspaper, Video, Mic } from 'lucide-react';

// Tag icon mapping
const tagIcons = {
  News: Newspaper,
  Article: FileText,
  Video: Video,
  Interview: Mic,
};

// Hook to fetch In The News items from Contentful
export const useInTheNews = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const spaceId = process.env.REACT_APP_CONTENTFUL_SPACE_ID;
        const accessToken = process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN;
        
        if (spaceId && accessToken) {
          const response = await fetch(
            `https://cdn.contentful.com/spaces/${spaceId}/environments/master/entries?content_type=inTheNews&order=-fields.date`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            
            // Map assets for thumbnails
            const assets = {};
            if (data.includes?.Asset) {
              data.includes.Asset.forEach(asset => {
                assets[asset.sys.id] = `https:${asset.fields.file.url}`;
              });
            }
            
            const items = data.items?.map(item => ({
              id: item.sys.id,
              title: item.fields.title,
              description: item.fields.description,
              link: item.fields.link,
              date: item.fields.date,
              tags: item.fields.tags || [],
              thumbnail: item.fields.thumbnail?.sys?.id 
                ? assets[item.fields.thumbnail.sys.id] 
                : null,
            })) || [];
            
            setNewsItems(items);
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.warn('Failed to fetch news items:', error);
        setIsLoading(false);
      }
    };

    fetchNews();
  }, []);

  return { newsItems, isLoading };
};

// In The News Section Component
export const InTheNewsSection = ({ showEmptyState = false }) => {
  const { newsItems, isLoading } = useInTheNews();
  const [selectedTag, setSelectedTag] = useState('All');

  if (isLoading) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="w-8 h-8 border-4 border-brand-secondary border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </section>
    );
  }

  if (newsItems.length === 0) {
    // Show placeholder for empty state
    return (
      <section className="py-20 bg-white" data-testid="in-the-news-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-4">
              In The News
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto mb-8">
              Featured articles, interviews, and media coverage.
            </p>
            <div className="bg-brand-accent rounded-2xl p-8 max-w-md mx-auto">
              <Newspaper className="w-12 h-12 text-brand-secondary/40 mx-auto mb-4" />
              <p className="text-slate-500 text-sm">
                News content coming soon. Add items in Contentful to display them here.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  const allTags = ['All', ...new Set(newsItems.flatMap(item => item.tags))];
  
  const filteredNews = selectedTag === 'All' 
    ? newsItems 
    : newsItems.filter(item => item.tags.includes(selectedTag));

  return (
    <section className="py-20 bg-white" data-testid="in-the-news-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-4">
            In The News
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Featured articles, interviews, and media coverage.
          </p>
        </motion.div>

        {/* Tag Filters */}
        {allTags.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {allTags.map(tag => {
              const TagIcon = tagIcons[tag] || Tag;
              return (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedTag === tag
                      ? 'bg-brand-secondary text-white'
                      : 'bg-brand-accent text-brand-primary hover:bg-brand-secondary/10'
                  }`}
                >
                  {tag !== 'All' && <TagIcon className="w-4 h-4" />}
                  {tag}
                </button>
              );
            })}
          </div>
        )}

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNews.map((item, index) => (
            <motion.a
              key={item.id}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group block bg-brand-accent rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Thumbnail */}
              {item.thumbnail && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={item.thumbnail}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              
              <div className="p-5">
                {/* Tags */}
                {item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.tags.map(tag => {
                      const TagIcon = tagIcons[tag] || Tag;
                      return (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 text-xs font-medium text-brand-secondary bg-brand-secondary/10 px-2 py-1 rounded-full"
                        >
                          <TagIcon className="w-3 h-3" />
                          {tag}
                        </span>
                      );
                    })}
                  </div>
                )}
                
                {/* Title */}
                <h3 className="font-heading text-lg font-bold text-brand-primary group-hover:text-brand-secondary transition-colors mb-2 line-clamp-2">
                  {item.title}
                </h3>
                
                {/* Description */}
                {item.description && (
                  <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                    {item.description}
                  </p>
                )}
                
                {/* Date & Link */}
                <div className="flex items-center justify-between text-sm">
                  {item.date && (
                    <span className="flex items-center gap-1 text-slate-500">
                      <Calendar className="w-4 h-4" />
                      {new Date(item.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-brand-secondary font-medium">
                    Read More <ExternalLink className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InTheNewsSection;
